export const CONVENTION_REGISTRATION_DRAFT_STORAGE_KEY =
  'convention-registration-pending'

export type ConventionSelectedUpsell = {
  productId: number
  optionId: number | null
}

export type ConventionRegistrationDraft = {
  conventionSlug?: string
  conventionProductId: number
  registrationOptionId: number | null
  registrantsCount: number
  names: string[]
  workshops: number[][]
  upsells: Array<ConventionSelectedUpsell | null>
  usePaymentPlan: boolean
  shouldAutoContinue: boolean
  acceptedTerms: boolean
}

type ConventionRegistrationDraftMatch = {
  conventionSlug?: string
  conventionProductId: number
  registrationOptionId: number | null
  registrantsCount: number
}

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const numberOrZero = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeNames = (value: unknown): string[] => {
  return Array.isArray(value) ? value.map(name => String(name ?? '')) : []
}

const normalizeWorkshops = (value: unknown): number[][] => {
  if (!Array.isArray(value)) return []

  return value.map(registrantWorkshops => {
    if (!Array.isArray(registrantWorkshops)) return []
    return registrantWorkshops
      .map(workshopId => Number(workshopId))
      .filter(workshopId => Number.isFinite(workshopId))
  })
}

const normalizeUpsells = (
  value: unknown
): Array<ConventionSelectedUpsell | null> => {
  if (!Array.isArray(value)) return []

  return value.map(upsell => {
    if (!upsell || typeof upsell !== 'object') return null
    const productId = numberOrZero((upsell as any).productId)
    if (productId <= 0) return null

    return {
      productId,
      optionId: numberOrNull((upsell as any).optionId),
    }
  })
}

const normalizeDraft = (value: unknown): ConventionRegistrationDraft | null => {
  if (!value || typeof value !== 'object') return null

  const raw = value as any
  const conventionProductId = numberOrZero(raw.conventionProductId)
  const registrantsCount = numberOrZero(raw.registrantsCount)

  if (conventionProductId <= 0 || registrantsCount <= 0) return null

  return {
    conventionSlug:
      typeof raw.conventionSlug === 'string' ? raw.conventionSlug : undefined,
    conventionProductId,
    registrationOptionId: numberOrNull(raw.registrationOptionId),
    registrantsCount,
    names: normalizeNames(raw.names),
    workshops: normalizeWorkshops(raw.workshops),
    upsells: normalizeUpsells(raw.upsells),
    usePaymentPlan: Boolean(raw.usePaymentPlan),
    shouldAutoContinue: Boolean(raw.shouldAutoContinue ?? raw.shouldAutoSubmit),
    acceptedTerms: Boolean(raw.acceptedTerms),
  }
}

export const draftMatchesRegistration = (
  draft: ConventionRegistrationDraft,
  match: ConventionRegistrationDraftMatch
) => {
  return (
    draft.conventionSlug === match.conventionSlug &&
    draft.conventionProductId === match.conventionProductId &&
    draft.registrationOptionId === match.registrationOptionId &&
    draft.registrantsCount === match.registrantsCount
  )
}

export const loadConventionRegistrationDraft = (
  match: ConventionRegistrationDraftMatch
) => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.sessionStorage.getItem(
      CONVENTION_REGISTRATION_DRAFT_STORAGE_KEY
    )
    if (!raw) return null

    const draft = normalizeDraft(JSON.parse(raw))
    if (!draft || !draftMatchesRegistration(draft, match)) return null

    return draft
  } catch {
    return null
  }
}

export const saveConventionRegistrationDraft = (
  draft: ConventionRegistrationDraft
) => {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(
      CONVENTION_REGISTRATION_DRAFT_STORAGE_KEY,
      JSON.stringify(draft)
    )
  } catch (e) {
    console.error('Failed to save registration data:', e)
  }
}

export const clearConventionRegistrationDraft = () => {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.removeItem(CONVENTION_REGISTRATION_DRAFT_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export const buildConventionRegistrationParams = ({
  conventionProductId,
  registrationOptionId,
  registrantsCount,
  bypassSoldOut = false,
}: {
  conventionProductId: number
  registrationOptionId: number | null
  registrantsCount: number
  bypassSoldOut?: boolean
}) => {
  const params = new URLSearchParams()
  params.set('conventionProductId', conventionProductId.toString())
  if (registrationOptionId != null) {
    params.set('registrationOptionId', registrationOptionId.toString())
  }
  params.set('count', registrantsCount.toString())
  if (bypassSoldOut) params.set('bypassSoldOut', 'true')

  return params
}
